import { Button, Card, Divider, Popconfirm, Spin, Tree } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { IUserSystem } from "../../models/Users";
import { useGetAllPermissions } from "../../query/PermissionQuery";
import {
  useAuthorizeUser,
  useGetPermissionsByUser,
} from "../../query/UserQuery";
import { notify } from "../../utils/toast";

interface PermissionTreeProps {
  selectedUser: IUserSystem;
}

const PermissionTree: React.FC<PermissionTreeProps> = ({ selectedUser }) => {
  const [checkedPermissions, setCheckedPermissions] = useState<number[]>([]);

  const { data: permissions, isLoading: isLoadingPermissions } =
    useGetAllPermissions();
  const authorizedPermissions = useAuthorizeUser();
  const { data: userPermission, isLoading: isLoadingUserPermission } =
    useGetPermissionsByUser(selectedUser.id);

  useEffect(() => {
    if (userPermission?.permissions) {
      setCheckedPermissions(userPermission.permissions.map((p) => p.id));
    }
  }, [selectedUser, userPermission]);

  const selectedPermissionLabels = useMemo(() => {
    if (!permissions) return [];

    const checkedSet = new Set(checkedPermissions.map((id) => id.toString()));
    const result: string[] = [];

    permissions.forEach((group) => {
      group.children?.forEach((child) => {
        if (checkedSet.has(child.key)) {
          result.push(`${group.title} - ${child.title}`);
        }
      });
    });

    return result;
  }, [checkedPermissions, permissions]);

  if (isLoadingPermissions || isLoadingUserPermission) {
    return (
      <Card title="Phân quyền người dùng">
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  return (
    <>
      <Popconfirm
        title="Xác nhận thay đổi quyền?"
        description={
          <div style={{ marginTop: 8, width: 300 }}>
            <ul style={{ marginTop: 4, maxHeight: 120, overflowY: "auto" }}>
              {selectedPermissionLabels.map((label, idx) => (
                <li
                  key={idx}
                  className="font-bold text-green-600"
                  style={{ fontSize: 12 }}
                >
                  {label}
                </li>
              ))}
            </ul>
          </div>
        }
        onConfirm={async () => {
          try {
            await authorizedPermissions.mutateAsync({
              id: selectedUser.id,
              permission: checkedPermissions,
            });
            notify.success("Cập nhật quyền thành công");
          } catch {
            notify.error("Cập nhật quyền thất bại");
          }
        }}
        okText="Xác nhận"
        cancelText="Hủy"
      >
        <Button type="primary">Xác nhận chọn các quyền</Button>
      </Popconfirm>

      <Divider variant="dashed" style={{ borderColor: "#7cb305" }} dashed>
        Danh sách quyền
      </Divider>

      <Tree
        checkable
        defaultExpandAll
        selectable={false}
        checkedKeys={checkedPermissions.map((id) => id.toString())}
        onCheck={(checked) => {
          const ids = (checked as string[]).map((id) => Number(id));
          setCheckedPermissions(ids);
        }}
        treeData={permissions || []}
        height={660}
        style={{ width: "100%" }}
        titleRender={(nodeData) => <div>{nodeData.title}</div>}
      />
    </>
  );
};

export default PermissionTree;
